import React from 'react';

import { FlexColumn, FlexRow } from '../../components';
import _ from '../../locale';
import { LOWER_KEY, UPPER_KEY, VALUE_KEY } from '../../models/data-key';
import { styled } from '../../theme';
import themeConfig from '../../theme/theme-config';
import formatChartValue from '../../utility/formatting/formatChartValue';

const Wrapper = styled(FlexColumn)`
    font-size: 14px;
    font-family: ${themeConfig.typography.fontFamily.medium};
    color: ${themeConfig.color.jetBlack};
`;

const HeaderWrapper = styled('div')`
    padding-bottom: 10px;
    border-bottom: 1px solid #dfe0e0;
    font-weight: ${themeConfig.typography.fontWeight.bold};
`;

const ContentWrapper = styled(FlexColumn)`
    padding-bottom: 12px;
`;

const FooterWrapper = styled(FlexRow)`
    padding-top: 12px;
    border-top: 1px solid #dfe0e0;
    justify-content: space-between;
`;

const FooterValuesRange = styled('div')`
    font-weight: ${themeConfig.typography.fontWeight.bold};
`;

const Item = styled(FlexRow)`
    padding-top: 12px;
    justify-content: space-between;
`;

const Label = styled('span')`
    max-width: 250px;
    margin-right: 12px;
    padding-left: 4px;
    white-space: initial;
    word-wrap: break-word;
`;

const Marker = styled('div')<{ index: number }>(
    ({ index }) => `
    position: relative;
    z-index: ${-index};

    :nth-of-type(n + 1) {
        margin-left: -6px;
    }
`
);

const MAX_TOOLTIP_VALUES = 4;

const getKey = (item) => Object.entries(item.encode).join();

const Header = ({ items }) => <HeaderWrapper>{items[0].name}</HeaderWrapper>;

const Content = ({ items, numberFormatter }) => {
    const { dimensionNames } = items[0];
    const getFilterIdx = (key) => dimensionNames.indexOf(key);

    const lowerIdx = getFilterIdx(LOWER_KEY);
    const upperIdx = getFilterIdx(UPPER_KEY);
    const valueIdx = getFilterIdx(VALUE_KEY);

    return (
        <ContentWrapper>
            {items.map((item) => {
                const { value: data, encode, marker, seriesName, seriesType } = item;
                const isHeatmap = seriesType === 'heatmap';
                const name = isHeatmap ? _(data[encode.y]) : seriesName;
                const value = numberFormatter(
                    isHeatmap ? data[encode.value] * 100 : data[valueIdx]
                );
                const lower = numberFormatter(data[lowerIdx]);
                const upper = numberFormatter(data[upperIdx]);
                const range = lower && upper ? ` (${lower} - ${upper})` : '';
                const unit = isHeatmap ? '%' : '';
                const content = `${value}${range}${unit}`;
                return (
                    <Item key={getKey(item)}>
                        <FlexRow>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: marker,
                                }}
                            />
                            <Label>{name}</Label>
                        </FlexRow>{' '}
                        <b>{content}</b>
                    </Item>
                );
            })}
        </ContentWrapper>
    );
};

const Footer = ({ items, numberFormatter }) => {
    const values = items.map(({ data, encode }) => data[encode.y]);
    const min = numberFormatter(Math.min(...values));
    const max = numberFormatter(Math.max(...values));

    return (
        <FooterWrapper>
            <FlexRow>
                {items.slice(0, 3).map((item, index) => (
                    <Marker
                        key={getKey(item)}
                        index={index}
                        dangerouslySetInnerHTML={{
                            __html: item.marker,
                        }}
                    />
                ))}
                <Label>+ {items.length} more</Label>
            </FlexRow>
            <FooterValuesRange>
                {min} to {max}
            </FooterValuesRange>
        </FooterWrapper>
    );
};

export const Tooltip = ({ items, numberFormatter = formatChartValue }) => {
    const withFooter = items.length > MAX_TOOLTIP_VALUES;
    const visibleItems = items.slice(0, MAX_TOOLTIP_VALUES);
    const hiddenItems = items.slice(MAX_TOOLTIP_VALUES);

    return (
        <Wrapper>
            <Header items={visibleItems} />
            <Content items={visibleItems} numberFormatter={numberFormatter} />
            {withFooter && <Footer items={hiddenItems} numberFormatter={numberFormatter} />}
        </Wrapper>
    );
};

export default Tooltip;
